<template>
  <div class="services--table-row">
    <div class="services--table-row-name">
      <div class="services--table-row-selection">
        <CheckboxCommon v-model="isSelected" :label="service.name" />
        <div class="services--table-row-more-info">
          <span>{{serviceDescriptionText}}</span>
          <div
            @click="toggleExpandedDescription()"
            v-if="truncateDescription && !expandedDescription">
            <v-icon>expand_more</v-icon>Show More
          </div>
          <div
            @click="toggleExpandedDescription()"
            v-if="truncateDescription && expandedDescription">
            <v-icon>expand_less</v-icon>Show Less
          </div>
          <ServiceDescriptionModal ref="serviceDescriptionModal"/>
        </div>
      </div>

      <div v-if="service.allowQuantity" class="services--table-row-quantity">
        <v-text-field
          v-model="hasQuantity"
          v-validate="{ numeric: true, min_value: 1 }"
          :data-vv-as="inputLabel"
          :data-vv-name="inputKey"
          :disabled="!isSelected"
          :error-messages="errors.collect(inputKey)"
          :hide-details="true"
          :name="inputKey"
          label="Quantity"
          outline
          placeholder=""
          ref="quantity" />
        {{service.quantityDescriptor}}
      </div>
    </div>

    <div class="services--table-row-divider">
      <hr/>
    </div>

    <div v-if="isShowServicePricesEnabled" class="services--table-row-fee">
      <p>{{currency(service.fee)}}</p>
    </div>
  </div>
</template>

<script>
import { Mixins } from '@spectorasoftware/homegauge-vue';

import CheckboxCommon from '@/components/common/CheckboxCommon.vue';
import FeatureFlagUtil from '@/utilities/feature-flag-util';
import ServiceDescriptionModal from '@/components/ServiceDescriptionModal.vue';

const { CurrencyFilter } = Mixins;

export default {
  components: {
    CheckboxCommon,
    ServiceDescriptionModal,
  },
  props: {
    quantity: {
      default: null,
    },
    selected: {
      type: Boolean,
      default: false,
    },
    service: {
      type: Object,
      require: true,
    },
  },
  mixins: [CurrencyFilter],
  inject: ['$validator'],
  mounted() {
    this.truncateDescription = this.service?.description?.length > 125;
  },
  methods: {
    toggleExpandedDescription() {
      this.expandedDescription = !this.expandedDescription;
    },
  },
  computed: {
    hasQuantity: {
      get() {
        return this.quantity;
      },
      set(value) {
        this.$emit('updateQuantity', { id: this.service.id, quantity: value });
      },
    },
    inputKey() {
      return `quantity_${this.service.id}`;
    },
    inputLabel() {
      return `${this.service.name} Quantity`;
    },
    isSelected: {
      get() {
        return this.selected;
      },
      set() {
        this.$emit('toggleService', this.service.id);
      },
    },
    serviceDescriptionText() {
      if (this.truncateDescription && !this.expandedDescription) {
        return `${this.service.description.substring(0, 125)}...`;
      }
      return this.service.description;
    },
  },
  data() {
    return {
      isShowServicePricesEnabled: FeatureFlagUtil.isShowServicePricesEnabled(),
      truncateDescription: false,
      expandedDescription: false,
    };
  },
};
</script>

<style lang="scss">
@import '../styles/mixins/breakpoints';
@import '../styles/vars/inputs';
@import '../styles/vars/typography';
@import '../styles/vars/theme';

$row-padding: 14px;

.services--table-row {
  display: flex;
  font-size: 1.143rem;
  letter-spacing: 0.014rem;

  > * {
    align-content: flex-start;
    vertical-align: top;
  }

  p {
    margin: 0;
  }

  &-divider {
    flex: 1;
    padding-top: $row-padding * 2;

    hr {
      border: none;
      border-top: 2px dotted #bfc4ce;
    }
  }

  &-fee {
    font-weight: $typography-weight-bold;
    letter-spacing: 0.07rem;
    line-height: 1.428;
    padding: $row-padding 0 $row-padding $row-padding;
    text-align: right;
  }

  &-name {
    flex: 0 1 100%;
    padding: $row-padding $row-padding $row-padding 0;

    p {
      line-height: 1.25;
    }

    ::v-deep .v-input--selection-controls .v-input__control {
      overflow: hidden;
    }
  }

  &-quantity {
    font-size: 1rem;
    padding: 12px 0 12px ($checkbox-size * 2);

    > * {
      vertical-align: bottom;
    }

    .v-text-field {
      border-radius: 5px;
      display: inline-block;
      font-size: 1.1rem;
      letter-spacing: 0.023rem;
      margin: 0 0.6rem 0 0;

      &.v-input--is-disabled {
        background: $input-background-disabled;
      }

      .v-input__slot {
        width: 7rem;
      }
    }
  }

  &-more-info {
    padding-left: $checkbox-size + ($checkbox-size - 4);
    span {
      font-size: 13px;
      line-height: 20px;
      color: $theme-color-mono-60;
    }
    div {
      display: flex;
      align-items: center;
      font-size: 11px;
      font-weight: 600;
      line-height: 20px;
      color: $theme-color-mono-45;
      cursor: pointer;
    }
    p {
      line-height: 1.25;
    }
  }
}

@include responsive-breakpoint(sm) {
  .services--table-row {
    &-fee {
      border: none;
    }

    &-name {
      border: none;
    }

    &-quantity {
      padding-left: $checkbox-size + ($checkbox-size / 2);
    }

    &-more-info {
      padding-left: $checkbox-size + ($checkbox-size / 2) - 4;
    }

    &-selection {
      margin-left: 0;

      .v-input--selection-controls__input {
        margin-right: ($checkbox-size / 2);
      }
    }
  }
}
</style>
